import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService } from '../../services/products';
import { useForm, validationRules } from '../../hooks/useForm';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { validateImageFile } from '../../utils/helpers';
import { ImageUtils } from '../../utils/imageUtils';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const alertTimeoutRef = useRef(null);

  // Auto-close alert functionality
  const setTimedAlert = (alertData, duration = 5000) => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }
    setAlert(alertData);
    if (duration > 0) {
      alertTimeoutRef.current = setTimeout(() => {
        setAlert(null);
        alertTimeoutRef.current = null;
      }, duration);
    }
  };

  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    setValues
  } = useForm(
    {
      name: '',
      description: '',
      price: '',
      stock_quantity: '',
      category: '',
      subcategory: '',
      is_active: true,
      weight_kg: '',
      length_cm: '',
      width_cm: '',
      height_cm: '',
      volumetric_weight_kg: ''
    },
    {
      name: [validationRules.required, validationRules.maxLength(100)],
      description: [validationRules.required, validationRules.maxLength(1000)],
      price: [validationRules.required, validationRules.price],
      stock_quantity: [validationRules.required, validationRules.stock],
      category: [validationRules.required]
    }
  );

  useEffect(() => {
    loadData();
    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const categoriesData = await productService.getCategories();
      setCategories(categoriesData.results || categoriesData);

      if (isEditing) {
        const productData = await productService.getProduct(id);
        setValues({
          name: productData.name || '',
          description: productData.description || '',
          price: productData.base_price || '',
          stock_quantity: productData.quantity || '',
          category: productData.category || '',
          subcategory: productData.subcategory || '',
          is_active: productData.status === 'active',
          weight_kg: productData.weight_kg || '',
          length_cm: productData.length_cm || '',
          width_cm: productData.width_cm || '',
          height_cm: productData.height_cm || '',
          volumetric_weight_kg: productData.volumetric_weight_kg || ''
        });
        setExistingImages(productData.images || []);

        if (productData.category) {
          loadSubcategories(productData.category);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setTimedAlert({
        type: 'danger',
        message: 'Failed to load product data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    try {
      setLoadingSubcategories(true);
      const subcategoriesData = await productService.getSubcategories(categoryId);
      setSubcategories(subcategoriesData.results || subcategoriesData);
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    handleChange('category', categoryId);
    handleChange('subcategory', '');
    loadSubcategories(categoryId);
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const processedFiles = [];
    const errors = [];

    for (const file of files) {
      try {
        const basicError = await validateImageFile(file);
        if (basicError && !basicError.includes('must be exactly 500×500px')) {
          errors.push(`${file.name}: ${basicError}`);
          continue;
        }

        const { ImageUtils } = await import('../../utils/imageUtils');
        const validation = await ImageUtils.validateProductImageDimensions(file);

        let finalFile = file;

        if (!validation.isValid) {
          setTimedAlert({
            type: 'info',
            message: `Auto-resizing ${file.name} from ${validation.width}×${validation.height}px to 500×500px...`
          }, 3000);

          try {
            finalFile = await ImageUtils.resizeProductImage(file);
            setTimedAlert({
              type: 'success',
              message: `✅ ${file.name} automatically resized to 500×500px!`
            }, 2000);
          } catch (resizeError) {
            console.error('Auto-resize failed:', resizeError);
            errors.push(`${file.name}: Failed to auto-resize. Please manually resize to 500×500px.`);
            continue;
          }
        }

        processedFiles.push(finalFile);
      } catch (error) {
        console.error('Error processing image:', error);
        errors.push(`${file.name}: Error processing image`);
      }
    }

    if (errors.length > 0) {
      setTimedAlert({
        type: 'danger',
        message: 'Image validation errors:\n' + errors.join('\n')
      });
    }

    if (processedFiles.length === 0) {
      return;
    }

    const totalImages = existingImages.length + selectedImages.length + processedFiles.length;
    if (totalImages > 5) {
      setTimedAlert({
        type: 'warning',
        message: 'You can only upload up to 5 images per product.'
      });
      return;
    }

    setSelectedImages([...selectedImages, ...processedFiles]);
  };

  const removeSelectedImage = (index) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageId) => {
    try {
      await productService.deleteProductImage(imageId);
      setExistingImages(existingImages.filter(img => img.id !== imageId));
      setTimedAlert({
        type: 'success',
        message: 'Image removed successfully!'
      });
    } catch (error) {
      setTimedAlert({
        type: 'danger',
        message: 'Failed to remove image. Please try again.'
      });
    }
  };

  const uploadImages = async (productId) => {
    if (selectedImages.length === 0) return;

    console.log('ProductForm: uploadImages called with productId:', productId);
    setUploadingImages(true);
    try {
      for (const image of selectedImages) {
        console.log('ProductForm: Uploading image:', image.name, 'for product:', productId);
        await productService.uploadProductImage(productId, image);
      }
      setSelectedImages([]);
    } catch (error) {
      console.error('Error uploading images:', error);

      let errorMessage = 'Failed to upload images.';
      if (error.response?.status === 404) {
        errorMessage = 'Image upload endpoint not found. Please check the product ID.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setTimedAlert({
        type: 'warning',
        message: `Product created successfully, but ${errorMessage}`
      });
    } finally {
      setUploadingImages(false);
    }
  };

  const onSubmit = async (formData) => {
    try {
      const transformedData = {
        name: formData.name,
        description: formData.description,
        base_price: parseFloat(formData.price),
        quantity: parseInt(formData.stock_quantity),
        category: formData.category,
        subcategory: formData.subcategory || null,
        status: formData.is_active ? 'active' : 'inactive',
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
        length_cm: formData.length_cm ? parseFloat(formData.length_cm) : null,
        width_cm: formData.width_cm ? parseFloat(formData.width_cm) : null,
        height_cm: formData.height_cm ? parseFloat(formData.height_cm) : null,
        volumetric_weight_kg: formData.volumetric_weight_kg ? parseFloat(formData.volumetric_weight_kg) : null
      };

      let productData;

      if (isEditing) {
        productData = await productService.updateProduct(id, transformedData);
        setTimedAlert({
          type: 'success',
          message: 'Product updated successfully!'
        });
      } else {
        productData = await productService.createProduct(transformedData);
        console.log('ProductForm: Product created, data:', productData);
        setTimedAlert({
          type: 'success',
          message: 'Product created successfully!'
        });
      }

      if (selectedImages.length > 0) {
        console.log('ProductForm: About to upload images for productId:', productData.id);
        try {
          await uploadImages(productData.id);
        } catch (imageError) {
          console.warn('Product saved but image upload failed:', imageError);
        }
      }

      setTimeout(() => {
        navigate('/products');
      }, 2000);
    } catch (error) {
      console.error('Error saving product:', error);

      let errorMessage = 'Failed to save product. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data;

        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else {
          const fieldErrors = [];
          Object.keys(errorData).forEach(field => {
            if (Array.isArray(errorData[field])) {
              fieldErrors.push(`${field}: ${errorData[field].join(', ')}`);
            } else {
              fieldErrors.push(`${field}: ${errorData[field]}`);
            }
          });

          if (fieldErrors.length > 0) {
            errorMessage = `Validation errors: ${fieldErrors.join('; ')}`;
          }
        }
      }

      setTimedAlert({
        type: 'danger',
        message: errorMessage
      });
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading product data..." />;
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all"
            onClick={() => navigate('/products')}
            style={{ backgroundColor: '#161e35', color: '#f8fafc' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e2a47'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#161e35'}
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              {isEditing ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              {isEditing ? 'Update your product information' : 'Create a new product for your store'}
            </p>
          </div>
        </div>

        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        {/* Form Card */}
        <div className="rounded-2xl p-8 shadow-xl" style={{ backgroundColor: '#161e35' }}>
          <form onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(onSubmit);
          }}>
            {/* Basic Information */}
            <div className="mb-8">
              <h5 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Basic Information</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Product Name *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all"
                    style={{
                      backgroundColor: '#0f1420',
                      color: '#f8fafc',
                      border: errors.name ? '1px solid #ef4444' : 'none'
                    }}
                    id="name"
                    value={values.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Enter product name"
                  />
                  {errors.name && (
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{errors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="category" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Category *
                  </label>
                  <select
                    className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all appearance-none"
                    style={{
                      backgroundColor: '#0f1420',
                      color: '#f8fafc',
                      border: errors.category ? '1px solid #ef4444' : 'none'
                    }}
                    id="category"
                    value={values.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{errors.category}</p>
                  )}
                </div>
              </div>

              {/* Subcategory */}
              {values.category && (
                <div className="mt-4">
                  <label htmlFor="subcategory" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Subcategory {loadingSubcategories && <span className="ml-2">Loading...</span>}
                  </label>
                  <select
                    className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all appearance-none md:w-1/3"
                    style={{
                      backgroundColor: '#0f1420',
                      color: '#f8fafc',
                      border: 'none'
                    }}
                    id="subcategory"
                    value={values.subcategory}
                    onChange={(e) => handleChange('subcategory', e.target.value)}
                    disabled={loadingSubcategories || subcategories.length === 0}
                  >
                    <option value="">
                      {subcategories.length === 0 ? 'No subcategories available' : 'Select Subcategory (Optional)'}
                    </option>
                    {subcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                    Optionally select a subcategory for better organization
                  </p>
                </div>
              )}

              {/* Description */}
              <div className="mt-4">
                <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                  Description *
                </label>
                <textarea
                  className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all"
                  style={{
                    backgroundColor: '#0f1420',
                    color: '#f8fafc',
                    border: errors.description ? '1px solid #ef4444' : 'none'
                  }}
                  id="description"
                  rows="4"
                  value={values.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe your product in detail..."
                ></textarea>
                {errors.description && (
                  <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{errors.description}</p>
                )}
                <p className="text-xs mt-1 text-right" style={{ color: '#64748b' }}>
                  {values.description.length}/1000 characters
                </p>
              </div>
            </div>

            {/* Pricing & Inventory */}
            <div className="mb-8">
              <h5 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Pricing & Inventory</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="price" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Price (₹) *
                  </label>
                  <div className="flex">
                    <span className="px-4 py-3 rounded-l-xl text-sm font-bold" style={{ backgroundColor: '#0f1420', color: '#94a3b8' }}>₹</span>
                    <input
                      type="number"
                      className="flex-1 px-4 py-3 rounded-r-xl text-sm font-medium outline-none transition-all"
                      style={{
                        backgroundColor: '#0f1420',
                        color: '#f8fafc',
                        border: errors.price ? '1px solid #ef4444' : 'none'
                      }}
                      id="price"
                      step="0.01"
                      min="0"
                      value={values.price}
                      onChange={(e) => handleChange('price', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.price && (
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{errors.price}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="stock_quantity" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all"
                    style={{
                      backgroundColor: '#0f1420',
                      color: '#f8fafc',
                      border: errors.stock_quantity ? '1px solid #ef4444' : 'none'
                    }}
                    id="stock_quantity"
                    min="0"
                    value={values.stock_quantity}
                    onChange={(e) => handleChange('stock_quantity', e.target.value)}
                    placeholder="0"
                  />
                  {errors.stock_quantity && (
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{errors.stock_quantity}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="mb-8">
              <h5 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">
                <i className="bi bi-box-seam mr-2"></i>Package Details
              </h5>
              <div className="p-6 rounded-xl" style={{ backgroundColor: '#0f1420' }}>
                {/* Weight */}
                <div className="mb-4">
                  <label htmlFor="weight_kg" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Weight (kg) *
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      className="flex-1 px-4 py-3 rounded-l-xl text-sm font-medium outline-none transition-all"
                      style={{
                        backgroundColor: '#161e35',
                        color: '#f8fafc',
                        border: 'none'
                      }}
                      id="weight_kg"
                      step="0.1"
                      min="0"
                      value={values.weight_kg}
                      onChange={(e) => handleChange('weight_kg', e.target.value)}
                      placeholder="e.g., 5.2, 0.3, 100.9"
                      required
                    />
                    <span className="px-4 py-3 rounded-r-xl text-sm font-bold" style={{ backgroundColor: '#161e35', color: '#94a3b8' }}>kg</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                    Accept one decimal place (e.g., 5.2, 0.3, 100.9)
                  </p>
                </div>

                {/* Dimensions */}
                <div className="mb-4">
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Dimensions (cm) *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <input
                        type="number"
                        className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all"
                        style={{
                          backgroundColor: '#161e35',
                          color: '#f8fafc',
                          border: 'none'
                        }}
                        step="0.1"
                        min="0"
                        value={values.length_cm}
                        onChange={(e) => handleChange('length_cm', e.target.value)}
                        placeholder="Length"
                        required
                      />
                      <p className="text-xs mt-1" style={{ color: '#64748b' }}>Length (cm)</p>
                    </div>
                    <div>
                      <input
                        type="number"
                        className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all"
                        style={{
                          backgroundColor: '#161e35',
                          color: '#f8fafc',
                          border: 'none'
                        }}
                        step="0.1"
                        min="0"
                        value={values.width_cm}
                        onChange={(e) => handleChange('width_cm', e.target.value)}
                        placeholder="Width"
                        required
                      />
                      <p className="text-xs mt-1" style={{ color: '#64748b' }}>Width (cm)</p>
                    </div>
                    <div>
                      <input
                        type="number"
                        className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all"
                        style={{
                          backgroundColor: '#161e35',
                          color: '#f8fafc',
                          border: 'none'
                        }}
                        step="0.1"
                        min="0"
                        value={values.height_cm}
                        onChange={(e) => handleChange('height_cm', e.target.value)}
                        placeholder="Height"
                        required
                      />
                      <p className="text-xs mt-1" style={{ color: '#64748b' }}>Height (cm)</p>
                    </div>
                  </div>
                </div>

                {/* Volumetric Weight */}
                <div>
                  <label htmlFor="volumetric_weight_kg" className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#94a3b8' }}>
                    Volumetric Weight (kg)
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      className="flex-1 px-4 py-3 rounded-l-xl text-sm font-medium outline-none transition-all"
                      style={{
                        backgroundColor: '#161e35',
                        color: '#f8fafc',
                        border: 'none'
                      }}
                      id="volumetric_weight_kg"
                      step="0.1"
                      min="0"
                      value={values.volumetric_weight_kg}
                      onChange={(e) => handleChange('volumetric_weight_kg', e.target.value)}
                      placeholder="Auto-calculated or enter manually"
                    />
                    <span className="px-4 py-3 rounded-r-xl text-sm font-bold" style={{ backgroundColor: '#161e35', color: '#94a3b8' }}>kg</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                    Typically calculated as (Length × Width × Height) / 5000
                  </p>
                </div>
              </div>
            </div>

            {/* Product Status */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded cursor-pointer"
                  style={{
                    backgroundColor: values.is_active ? '#ef4444' : '#0f1420',
                    border: 'none'
                  }}
                  id="is_active"
                  checked={values.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <label className="text-white font-medium cursor-pointer" htmlFor="is_active">
                  Active Product
                </label>
              </div>
              <p className="text-xs mt-1 ml-8" style={{ color: '#64748b' }}>
                Active products are visible to customers and can be purchased.
              </p>
            </div>

            {/* Product Images */}
            <div className="mb-8">
              <h5 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Product Images</h5>
              
              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs mb-2" style={{ color: '#94a3b8' }}>Current Images:</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {existingImages.map((image) => (
                      <div key={image.id} className="relative">
                        <img
                          src={ImageUtils.getFullImageUrl(image.image)}
                          alt="Product"
                          className="w-full h-24 object-cover rounded-xl"
                          onError={(e) => {
                            e.target.src = ImageUtils.getPlaceholderImage();
                          }}
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: '#ef4444', color: '#fff' }}
                          onClick={() => removeExistingImage(image.id)}
                        >
                          <i className="bi bi-x text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Images Preview */}
              {selectedImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs mb-2" style={{ color: '#94a3b8' }}>New Images to Upload:</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {selectedImages.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt="Preview"
                          className="w-full h-24 object-cover rounded-xl"
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: '#ef4444', color: '#fff' }}
                          onClick={() => removeSelectedImage(index)}
                        >
                          <i className="bi bi-x text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Image Upload */}
              {(existingImages.length + selectedImages.length) < 5 && (
                <div>
                  <input
                    type="file"
                    className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all"
                    style={{
                      backgroundColor: '#0f1420',
                      color: '#f8fafc',
                      border: 'none'
                    }}
                    id="images"
                    multiple
                    accept="image/*"
                    onChange={handleImageSelect}
                  />
                  <div className="mt-2">
                    <p className="text-xs font-bold mb-1" style={{ color: '#3b82f6' }}>
                      <i className="bi bi-info-circle mr-1"></i>
                      Auto-resized to 500×500px
                    </p>
                    <p className="text-xs" style={{ color: '#64748b' }}>
                      Upload up to 5 images (JPEG, PNG, GIF). Images will be automatically resized to 500×500px. Maximum 5MB per image.
                      <br />
                      Currently: {existingImages.length + selectedImages.length}/5 images
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all"
                style={{
                  backgroundColor: '#1e2a47',
                  color: '#f8fafc'
                }}
                onClick={() => navigate('/products')}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d3a5a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1e2a47'}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all"
                style={{
                  backgroundColor: '#ef4444',
                  color: '#fff'
                }}
                disabled={isSubmitting || uploadingImages}
                onMouseEnter={(e) => !isSubmitting && !uploadingImages && (e.currentTarget.style.backgroundColor = '#dc2626')}
                onMouseLeave={(e) => !isSubmitting && !uploadingImages && (e.currentTarget.style.backgroundColor = '#ef4444')}
              >
                {isSubmitting || uploadingImages ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    {uploadingImages ? 'Uploading Images...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle mr-2"></i>
                    {isEditing ? 'Update Product' : 'Create Product'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
